/**
 * First-login setup wizard (4 steps).
 *
 * Mockup: docs/onboarding-wizard-mockup.html. Shown once to admins of NEW
 * companies via the feature-intro rail (SETUP_WIZARD key). Skip is terminal —
 * there is no reopen path (the getting-started checklist that offered one was
 * pulled 2026-07-07), though vertical/band answers are draft-autosaved.
 *
 * Steps: 1) operation type (vertical)  2) team size  3) module picks
 *        4) invite the team (existing user-save flow sends registration emails).
 *
 * onComplete fires AFTER the server has persisted completion; the caller is
 * responsible for refreshing module/submodule state (labels + permissions
 * changed server-side). onSkip = caller dismisses the intro.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdownlist } from '@/components/Dropdownlist';
import { useTheme } from '@/contexts/ThemeContext';
import {
  completeSetupWizard,
  getPermissionGroups,
  getSetupWizardStatus,
  saveSetupWizardDraft,
  saveUserManagement,
} from '@/services/api';

export interface SetupWizardModalProps {
  theme?: string;
  onComplete: () => void;
  onSkip: () => void;
}

interface VerticalDef {
  value: string;
  title: string;
  desc: string;
  toursDefault: boolean;
}

const VERTICALS: VerticalDef[] = [
  { value: 'patrol', title: 'Patrol security company', desc: 'Mobile patrol, alarm response, guard tours', toursDefault: true },
  { value: 'standing_guard', title: 'Standing guard (site & post)', desc: 'Officers posted at fixed sites', toursDefault: true },
  { value: 'campus', title: 'Campus / facility safety', desc: 'Schools, hospitals, corporate campuses', toursDefault: true },
  { value: 'ems', title: 'EMS / medical transport', desc: 'Ambulance and event medical dispatch', toursDefault: false },
  { value: 'tow', title: 'Tow & roadside', desc: 'Tow truck and roadside-assistance dispatch', toursDefault: false },
  { value: 'other', title: 'Something else', desc: "We'll keep the standard setup", toursDefault: true },
];

const BANDS = [
  { value: 'solo', label: 'Just me', sub: 'for now' },
  { value: 'small', label: '2–10', sub: 'team members' },
  { value: 'medium', label: '11–50', sub: 'team members' },
  { value: 'large', label: '50+', sub: 'team members' },
];

type InviteRole = 'officer' | 'dispatcher' | 'admin';

interface InviteRow {
  firstName: string;
  lastName: string;
  email: string;
  role: InviteRole;
}

const EMPTY_ROW: InviteRow = { firstName: '', lastName: '', email: '', role: 'officer' };

// A row counts as empty when no text field has been touched (role alone doesn't count —
// an untouched row is skipped by activeRows regardless of its dropdown value).
const isRowEmpty = (r: InviteRow) => !r.firstName.trim() && !r.lastName.trim() && !r.email.trim();
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// "Junior Admin" (not "Admin") so the label matches the permission-group title the
// invitee actually lands in — it's what the admin will see in User Management later.
const ROLE_ITEMS: { label: string; value: InviteRole }[] = [
  { label: 'Officer', value: 'officer' },
  { label: 'Dispatcher', value: 'dispatcher' },
  { label: 'Junior Admin', value: 'admin' },
];

// Seeded standard-group TYPE ids (RoleGroupInfo.TYPE_*) → invite roles. Matched by
// type because titles are display text an admin can rename; titles below are only
// the fallback for a response missing `type`.
const ROLE_GROUP_TYPES: Record<InviteRole, number> = {
  officer: 2,    // TYPE_MDT ("Mobile")
  dispatcher: 1, // TYPE_CAD ("Dispatch")
  admin: 5,      // TYPE_JUNIOR_ADMIN ("Junior Admin")
};

const ROLE_GROUP_TITLES: Record<InviteRole, string> = {
  officer: 'Mobile',
  dispatcher: 'Dispatch',
  admin: 'Junior Admin',
};

export function SetupWizardModal({ theme: themeProp, onComplete, onSkip }: SetupWizardModalProps) {
  const { theme: globalTheme } = useTheme();
  const theme = themeProp ?? globalTheme;

  const [step, setStep] = useState(1);
  // No default pre-selection: leaving these blank stores "unanswered" rather than
  // letting a click-through submit a pre-filled vertical/band as if it were chosen.
  const [vertical, setVertical] = useState<string>('');
  const [band, setBand] = useState<string>('');
  const [useTours, setUseTours] = useState(true);
  // Citations and client portal default ON to match the seeded groups
  // (createInitialPermissionGroups grants citation roles to the team groups and the
  // portal role to Admin): a click-through must not silently revoke anything.
  const [useCitations, setUseCitations] = useState(true);
  const [useClientPortal, setUseClientPortal] = useState(true);
  const [useAdvisories, setUseAdvisories] = useState(true);
  // One starting row: multiple blank rows read as "three people expected",
  // which is wrong for solo operators (step 2 offers "Just me"). "+ Add another"
  // covers bigger teams.
  const [rows, setRows] = useState<InviteRow[]>([{ ...EMPTY_ROW }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>('');
  const [inviteFailures, setInviteFailures] = useState<string[]>([]);
  const [serverDone, setServerDone] = useState(false);
  const [groups, setGroups] = useState<any[]>([]);
  // Whether the user has picked (or cleared) a vertical/band this session. Guards two races
  // against the async status pre-fill: a late pre-fill must not overwrite a fresh pick, and a
  // skip before the pre-fill resolves must not flush '' over a previously saved draft.
  const touchedRef = useRef(false);

  useEffect(() => {
    getPermissionGroups()
      .then((res: any) => {
        const list = Array.isArray(res) ? res : res?.groups ?? res?.permissionGroups ?? [];
        setGroups(Array.isArray(list) ? list : []);
      })
      .catch(() => setGroups([]));
  }, []);

  // Pre-fill from any autosaved draft so a returning admin resumes where they left off.
  useEffect(() => {
    getSetupWizardStatus()
      .then((st) => {
        if (touchedRef.current) return; // the user has already picked — their answers win
        if (st?.vertical) {
          setVertical(st.vertical);
          const def = VERTICALS.find((v) => v.value === st.vertical);
          if (def) setUseTours(def.toursDefault);
        }
        if (st?.fieldStaffBand) setBand(st.fieldStaffBand);
      })
      .catch(() => {});
  }, []);

  // Fire-and-forget autosave of the in-progress profile answers (non-destructive; the
  // backend ignores unknown values). Keeps vertical/band from being lost on skip or exit.
  const saveDraft = useCallback((v: string, b: string) => {
    saveSetupWizardDraft({ vertical: v, fieldStaffBand: b }).catch(() => {});
  }, []);

  const pickVertical = useCallback((v: VerticalDef) => {
    // Click the already-selected tile to clear it (back to no selection).
    const next = vertical === v.value ? '' : v.value;
    touchedRef.current = true;
    setVertical(next);
    if (next) setUseTours(v.toursDefault);
    saveDraft(next, band);
  }, [vertical, band, saveDraft]);

  const pickBand = useCallback((b: string) => {
    // Click the already-selected chip to clear it (back to no selection).
    const next = band === b ? '' : b;
    touchedRef.current = true;
    setBand(next);
    saveDraft(vertical, next);
  }, [vertical, band, saveDraft]);

  // Flush the current answers, then hand off to the caller's dismiss. Skip stays terminal
  // (marks the intro handled — no auto re-show); it just no longer loses what was entered.
  // Untouched sessions have nothing to flush — and sending their blank state would explicitly
  // clear a draft saved in an earlier session.
  const skipForNow = useCallback(() => {
    if (touchedRef.current) saveDraft(vertical, band);
    onSkip();
  }, [vertical, band, saveDraft, onSkip]);

  // Editing or removing rows after a failed send re-arms the send button —
  // the failure message promises a retry, so the interface must offer one
  // (serverDone guards against re-completing the wizard on the second pass).
  const setRow = useCallback((idx: number, patch: Partial<InviteRow>) => {
    setInviteFailures([]);
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }, []);

  const addRow = useCallback(() => setRows((prev) => [...prev, { ...EMPTY_ROW }]), []);
  // Removes the row, except the list never renders empty: the last remaining row is
  // CLEARED instead of removed (which also keeps the "Clear the row to skip it"
  // validation message honest — the x is the clear affordance).
  const removeRow = useCallback(
    (idx: number) => {
      setInviteFailures([]);
      setRows((prev) =>
        prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev.map(() => ({ ...EMPTY_ROW }))
      );
    },
    []
  );

  // Rows the user actually filled in (any field touched).
  const activeRows = useMemo(() => rows.filter((r) => !isRowEmpty(r)), [rows]);

  const groupIdForRole = useCallback(
    (role: InviteRole): number | null => {
      const byType = groups.find((g: any) => g?.type === ROLE_GROUP_TYPES[role]);
      if (byType?.id != null) return byType.id;
      const wanted = ROLE_GROUP_TITLES[role];
      const byTitle = groups.find((g: any) => (g?.title ?? '').toLowerCase() === wanted.toLowerCase());
      return byTitle?.id ?? null;
    },
    [groups]
  );

  const finish = useCallback(
    async (withInvites: boolean) => {
      if (busy) return;
      setError('');

      const invites = withInvites ? activeRows : [];
      if (withInvites) {
        const bad = invites.find(
          (r) => !r.firstName.trim() || !r.lastName.trim() || !EMAIL_RE.test(r.email.trim())
        );
        if (bad) {
          setError('Each invite needs a first name, last name, and a valid email. Clear the row to skip it.');
          return;
        }
      }

      setBusy(true);
      try {
        if (!serverDone) {
          await completeSetupWizard({
            vertical,
            fieldStaffBand: band,
            useTours,
            useCitations,
            useClientPortal,
            useAdvisories,
          });
          setServerDone(true);
        }

        const failures: string[] = [];
        for (const r of invites) {
          const groupId = groupIdForRole(r.role);
          if (groupId == null) {
            failures.push(r.email.trim());
            continue;
          }
          try {
            await saveUserManagement({
              firstName: r.firstName.trim(),
              lastName: r.lastName.trim(),
              email: r.email.trim(),
              permissionGroupIds: [groupId],
              isLoginEnabled: true,
            });
          } catch {
            failures.push(r.email.trim());
          }
        }

        if (failures.length > 0) {
          setInviteFailures(failures);
          setBusy(false);
          return; // stay open so the user sees which invites need redoing
        }
        onComplete();
      } catch {
        setError('Could not save your setup. Please try again.');
        setBusy(false);
      }
    },
    [busy, activeRows, serverDone, vertical, band, useTours, useCitations, useClientPortal, useAdvisories, groupIdForRole, onComplete]
  );

  // Body scroll-lock while open (the raw kstc shell doesn't do this itself —
  // BaseFormModal isn't used because it always offers X/Escape dismissal, and
  // the wizard's skip is terminal, so dismissal must stay an explicit choice).
  useEffect(() => {
    document.body.classList.add('kstc-scroll-lock');
    return () => document.body.classList.remove('kstc-scroll-lock');
  }, []);

  // Initial focus lands on the dialog so keyboard users start inside it.
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const stepTitles: Record<number, string> = {
    1: 'Which best describes your organization?',
    2: 'How many people are on your team?',
    3: "Which of these will you use? Turn off what you don't need.",
    4: 'Add your officers and dispatchers',
  };

  const renderDots = () => (
    <div className="ksw-dots">
      {[1, 2, 3, 4].map((n, i) => (
        <div key={n} style={{ display: 'contents' }}>
          {i > 0 && <span className={`ksw-dot-line${step >= n ? ' ksw-dot-line--done' : ''}`} />}
          <span className={`ksw-dot${step > n ? ' ksw-dot--done' : step === n ? ' ksw-dot--active' : ''}`}>
            {step > n ? '✓' : n}
          </span>
        </div>
      ))}
    </div>
  );

  // The app's standard toggle (same markup/classes as Add User's Login Enabled).
  const toggle = (on: boolean, set: (v: boolean) => void) => (
    <label className="kst-toggle">
      <input
        type="checkbox"
        className="kst-toggle-input"
        checked={on}
        disabled={busy}
        onChange={(e) => set(e.target.checked)}
      />
      <span className="kst-toggle-slider"></span>
    </label>
  );

  return (
    <div data-kstc-modal className={`kstc-backdrop ksw-modal ${theme}`.trim()}>
      <div ref={dialogRef} className="kstc-dialog" role="dialog" aria-modal="true" tabIndex={-1}>
        <div className="kstc-header">
          <h2 className="kstc-title">Welcome to Orna. Let's set up your workspace.</h2>
        </div>

        <div className="kstc-body">
          {renderDots()}
          <p className="ksw-step-title">{stepTitles[step]}</p>

          {step === 1 && (
            <div className="ksw-opt-grid">
              {VERTICALS.map((v) => (
                <button key={v.value} type="button" className={`ksw-opt${vertical === v.value ? ' ksw-opt--selected' : ''}`} onClick={() => pickVertical(v)} disabled={busy}>
                  <div className="ksw-opt-title">{v.title}</div>
                  <div className="ksw-opt-desc">{v.desc}</div>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <>
              <p className="ksw-lede">
                Count everyone who'll use Orna: officers in the field, dispatchers, and admins.
              </p>
              <div className="ksw-size-row">
                {BANDS.map((b) => (
                  <button key={b.value} type="button" className={`ksw-opt ksw-opt--chip${band === b.value ? ' ksw-opt--selected' : ''}`} onClick={() => pickBand(b.value)} disabled={busy}>
                    <div className="ksw-opt-title">{b.label}</div>
                    <div className="ksw-opt-desc">{b.sub}</div>
                  </button>
                ))}
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="ksw-mod-row">
                <div className="ksw-grow">
                  <div className="ksw-opt-title">Dispatch &amp; call cards</div>
                  <div className="ksw-opt-desc">Create, assign, and track calls in real time</div>
                </div>
                <span className="ksw-included">Included</span>
              </div>
              <div className="ksw-mod-row">
                <div className="ksw-grow">
                  <div className="ksw-opt-title">Reports</div>
                  <div className="ksw-opt-desc">Case, analytics, and daily activity reports</div>
                </div>
                <span className="ksw-included">Included</span>
              </div>
              <div className="ksw-mod-row">
                <div className="ksw-grow">
                  <div className="ksw-opt-title">Patrol tours</div>
                  <div className="ksw-opt-desc">Scheduled guard tours with stops at client locations</div>
                </div>
                {toggle(useTours, setUseTours)}
              </div>
              <div className="ksw-mod-row">
                <div className="ksw-grow">
                  <div className="ksw-opt-title">Citations</div>
                  <div className="ksw-opt-desc">Parking and violation citations</div>
                </div>
                {toggle(useCitations, setUseCitations)}
              </div>
              <div className="ksw-mod-row">
                <div className="ksw-grow">
                  <div className="ksw-opt-title">Client portal</div>
                  <div className="ksw-opt-desc">Give your clients a portal to see the reports you share</div>
                </div>
                {toggle(useClientPortal, setUseClientPortal)}
              </div>
              <div className="ksw-mod-row">
                <div className="ksw-grow">
                  <div className="ksw-opt-title">Advisories</div>
                  <div className="ksw-opt-desc">BOLOs and shift-to-shift notices</div>
                </div>
                {toggle(useAdvisories, setUseAdvisories)}
              </div>
              <div className="ksw-hint">
                Nothing is deleted, and you can turn these back on anytime. Finer control over every
                feature, including documents, messaging, and notes, is available per role in Admin
                under Permissions.
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <p className="ksw-lede">
                Each person gets an email invitation to set up their own login. Invites are free during
                your trial; after it, billing is per user with login enabled. You can disable a login
                anytime in User Management.
              </p>
              {rows.map((r, i) => {
                // The lone empty row has nothing to remove or clear — hide the x
                // rather than render a dead control (visibility keeps the layout
                // stable). On a lone FILLED row the x clears it (see removeRow).
                const removeHidden = rows.length === 1 && isRowEmpty(r);
                return (
                  <div key={i} className="ksw-inv-row">
                    <input className="kstc-input ksw-first" placeholder="First name" value={r.firstName} disabled={busy} onChange={(e) => setRow(i, { firstName: e.target.value })} />
                    <input className="kstc-input ksw-last" placeholder="Last name" value={r.lastName} disabled={busy} onChange={(e) => setRow(i, { lastName: e.target.value })} />
                    <input className="kstc-input ksw-email" placeholder="Email" value={r.email} disabled={busy} onChange={(e) => setRow(i, { email: e.target.value })} />
                    <div className="ksw-role">
                      <Dropdownlist
                        value={r.role}
                        items={ROLE_ITEMS}
                        sortItems={false}
                        searchable={false}
                        disabled={busy}
                        onChange={(v) => { if (v) setRow(i, { role: v as InviteRole }); }}
                      />
                    </div>
                    <button type="button" className="ksw-remove" onClick={() => removeRow(i)} disabled={busy || removeHidden} style={removeHidden ? { visibility: 'hidden' } : undefined} aria-label={rows.length > 1 ? 'Remove row' : 'Clear row'}>&times;</button>
                  </div>
                );
              })}
              <button type="button" className="ksw-link ksw-link--add" onClick={addRow} disabled={busy}>
                + Add another
              </button>
              <div className="ksw-hint">
                <b>Officers</b> work calls, tours, and reports from their device. <b>Dispatchers</b> create
                calls and dispatch officers to them. <b>Junior Admins</b> manage users and company
                settings. Permissions and billing stay with you until you promote them in Admin
                under Permissions.
              </div>
              {inviteFailures.length > 0 && (
                <div className="ksw-err">
                  Setup was saved, but these invites failed: {inviteFailures.join(', ')}. Edit the rows above
                  to send again, or close and add them later in User Management.
                </div>
              )}
            </>
          )}

          {error && <div className="ksw-err">{error}</div>}
        </div>

        <div className="kstc-actions ksw-actions">
          <button type="button" className="ksw-link" onClick={step === 4 ? () => finish(false) : skipForNow} disabled={busy}>
            {/* "Later in Admin" is only true on step 3 (module picks → Admin → Permissions);
                vertical and team size have no Admin surface, so steps 1–2 must not promise one. */}
            {step === 4 ? 'Finish without inviting' : step === 3 ? 'Set this up later in Admin' : 'Skip setup'}
          </button>
          <span className="ksw-spacer" />
          {step > 1 && (
            <button className="kstc-btn kstc-btn--secondary" type="button" onClick={() => setStep(step - 1)} disabled={busy}>
              Back
            </button>
          )}
          {step < 4 && (
            <button className="kstc-btn kstc-btn--primary" type="button" onClick={() => setStep(step + 1)} disabled={busy}>
              Next
            </button>
          )}
          {step === 4 && (
            <button className="kstc-btn kstc-btn--primary" type="button" onClick={() => (inviteFailures.length > 0 ? onComplete() : finish(true))} disabled={busy}>
              {busy
                ? 'Saving…'
                : inviteFailures.length > 0
                  ? 'Close'
                  : activeRows.length > 0
                    ? `Send ${activeRows.length} invite${activeRows.length === 1 ? '' : 's'} & finish`
                    : 'Finish'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
